/**
 * File: web/src/components/ToastProvider.tsx
 * =========================================
 *
 * Toast notifications (Context + hook + UI host)
 *
 * Purpose:
 * - Provide a lightweight, global notification system ("toasts") for the SPA.
 * - Allows any component/route to push transient messages without prop-drilling.
 *
 * When used:
 * - App.tsx wraps the application with <ToastProvider> so descendants can call useToast().
 * - Routes like Admin and UploadQueue use it to display success/errors (API calls, upload events).
 *
 * Design overview:
 * - React Context stores a small API: { push, dismiss, clear }.
 * - ToastProvider maintains a list of active toasts in React state.
 * - push() creates a toast with an id, adds it to state, and schedules auto-dismiss via setTimeout.
 *
 * Learner notes:
 * - Context is used here because it’s a cross-cutting concern: many unrelated components
 *   need to show notifications without sharing a parent/child relationship.
 * - useCallback/useMemo are used to stabilize function identities and the context value.
 *   This reduces unnecessary re-renders of consumers.
 *
 * Gotchas:
 * - setTimeout callbacks can fire after a component unmounts. In this app ToastProvider
 *   is mounted for the lifetime of the SPA, so it’s typically safe; if you ever mount/unmount
 *   it dynamically, consider cleaning up timers on unmount.
 * - crypto.randomUUID() is supported in modern browsers; if you need legacy browser support,
 *   swap to a fallback id generator.
 *
 * Accessibility:
 * - toast-host uses aria-live="polite" so screen readers announce new messages without
 *   interrupting the user aggressively.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type ToastKind = "success" | "info" | "warning" | "error";

/**
 * Toast model:
 * - ttlMs: time-to-live in milliseconds (auto-dismiss window)
 * - message is optional for short notifications that only need a title
 */
export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  message?: string;
  ttlMs?: number;
};

/**
 * Toast context API exposed to the rest of the app.
 *
 * push():
 * - caller supplies toast properties (except id)
 * - provider assigns id + default ttl and adds to state
 *
 * dismiss():
 * - removes a toast immediately (user clicks × or TTL expires)
 *
 * clear():
 * - removes all toasts (rarely used, but helpful for "reset UI state" actions)
 */
type ToastContextValue = {
  push: (t: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
  clear: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Custom hook for consuming toast API.
 *
 * Why a hook:
 * - Clean, consistent usage: const toast = useToast(); toast.push(...)
 * - Provides a helpful error if used outside the provider (developer feedback).
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export function ToastProvider(props: { children: React.ReactNode }) {
  /**
   * Active toasts state:
   * - Array ordering: newest at the top (we unshift new toast onto the front).
   * - Each toast has an id used as React key and dismiss handle.
   */
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * dismiss() removes a toast by id.
   *
   * useCallback:
   * - keeps the function identity stable across renders
   * - important because push() captures dismiss and schedules it in a timeout
   */
  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * clear() removes all toasts at once.
   * Provided mostly for completeness and potential future use.
   */
  const clear = useCallback(() => setToasts([]), []);

  /**
   * push() adds a toast and schedules auto-dismiss.
   *
   * Key behaviors:
   * - Generates a unique id so each toast can be dismissed individually.
   * - Applies a default TTL (5s) if caller doesn't specify ttlMs.
   * - Keeps at most 5 toasts to avoid UI overload.
   *
   * Learner note:
   * - The timeout calls dismiss(id) later. Since dismiss is stable (useCallback),
   *   the scheduled callback will still work correctly across renders.
   */
  const push = useCallback(
    (t: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();
      const ttl = t.ttlMs ?? 5000;

      // Normalize toast object with generated id and resolved ttl.
      const toast: Toast = { ...t, id, ttlMs: ttl };

      // Add newest toast to the front and cap list length.
      setToasts((prev) => [toast, ...prev].slice(0, 5));

      // Schedule auto-dismiss after TTL.
      window.setTimeout(() => dismiss(id), ttl);
    },
    [dismiss],
  );

  /**
   * Context value object.
   *
   * useMemo:
   * - ensures the value identity changes only when one of the functions changes
   * - helps prevent unnecessary re-renders in consumers of ToastContext
   */
  const value = useMemo(
    () => ({ push, dismiss, clear }),
    [push, dismiss, clear],
  );

  return (
    <ToastContext.Provider value={value}>
      {/* Render the application content first */}
      {props.children}

      {/* Toast "host" overlays/anchors toasts on the page.
          aria-live makes announcements accessible without being disruptive. */}
      <div className="toast-host" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role="status">
            <div className="toast-head">
              <div className="toast-title">{t.title}</div>

              {/* Dismiss button: immediate removal without waiting for TTL */}
              <button
                className="toast-x"
                type="button"
                aria-label="Dismiss"
                onClick={() => dismiss(t.id)}
              >
                x
              </button>
            </div>

            {/* Message is optional; many toasts only need a title */}
            {t.message ? <div className="toast-msg">{t.message}</div> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
